import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Grid,
  Box,
  Card,
  Stack,
  Typography,
  IconButton,
  Input,
  InputAdornment,
} from "@mui/material";
import dayjs from "dayjs";
import { get } from "aws-amplify/api";
import {
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import currentUserToken from "CurrentUserToken";
import SearchIcon from "@mui/icons-material/Search";
import { APIStatusDialog } from "components/APIStatusDialog";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};
const ClosureRulesDialog = ({ open, onClose, language }) => {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const toolbarRef = useRef(null);
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const [rowModesModel, setRowModesModel] = useState({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const tlmGetClosureRule = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/alarm/close/rule/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Error during the API call: ", error);
    }
  };

  useEffect(() => {
    if (open) {
      tlmGetClosureRule();
    }
  }, [open]);

  useEffect(() => {
    setColumnVisibilityModel({
      id: false
    });
  },[]);

  useEffect(() => {
    setSearchText(""); // Reset search text when the dialog is closed
  }, [onClose]);

  const columns = useMemo(() => {
    return [
      {
        field: "id",
        headerName: I18n.get("ID"),
        width: 100,
        editable: false,
      },
      {
        field: "description",
        headerName: I18n.get("Description"),
        width: 200,
        editable: false,
      },
      {
        field: "creator",
        headerName: I18n.get("Creator"),
        width: 150,
        editable: false,
      },
      {
        field: "last_modification",
        headerName: I18n.get("Last Modification Date"),
        width: 250,
        editable: false,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "last_modifier",
        headerName: I18n.get("Last Modifier"),
        width: 200,
        editable: false,
      },
      {
        field: "account_number",
        headerName: I18n.get("Account Number"),
        width: 160,
        editable: false,
      },
      {
        field: "original_store",
        headerName: I18n.get("Store"),
        width: 200,
        editable: false,
      },
      {
        field: "original_priority",
        headerName: I18n.get("Priority"),
        width: 100,
        editable: false,
      },
      {
        field: "original_equipment",
        headerName: I18n.get("Original Equipment"),
        width: 200,
        editable: false,
      },
      {
        field: "alarm_text",
        headerName: I18n.get("Alarm Text"),
        width: 200,
        editable: false,
      },
      {
        field: "severity",
        headerName: I18n.get("Severity"),
        width: 100,
        editable: false,
      },
      {
        field: "position",
        headerName: I18n.get("Position"),
        width: 100,
        editable: false,
      },
      
    ];
  }, [rowModesModel]);

  const handleColumnVisibilityChange = (newModel) => {
    const hiddenColumns = Object.keys(newModel).filter(
      (field) => !newModel[field]
    );
    localStorage.setItem("hiddenColumns", JSON.stringify(hiddenColumns));
  };

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower)
        );
    }
    return matchesAllFilters;
  };
  const filteredRows = state.audit.filter(applyStoreFiltering);
  
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" mt={0.5} mb={1} sx={{ pb: 2 }}>
            {I18n.get("Closure Rules")} (
            {filteredRows.length})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          justifyContent={tableParentIsMD ? "center" : "space-between"}
          spacing={2}
          sx={{ pb: 2 }}
        >
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                ml: 2,
                flexGrow: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Input
                type="text"
                placeholder={I18n.get("Search")}
                onChange={searchChanged}
                fullWidth={tableParentIsMD}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </Stack>
        </Stack>
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xxl">
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Card sx={{ p: 3 , height:"100%"}}>
            {renderToolbar()}
            <Box sx={{flexGrow:1}}>
              <DataGridPro
                sx={{ minHeight: "40vh", height:"100%"}}
                localeText={
                  language === "it"
                    ? itIT.components.MuiDataGrid.defaultProps.localeText
                    : language === "ge"
                    ? deDE.components.MuiDataGrid.defaultProps.localeText
                    : ""
                }
                onCellDoubleClick={(params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                rows={filteredRows}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                initialState={{
                  density: "compact",
                  pinnedColumns: { right: ["actions"] },
                }}
                apiRef={apiRef}
                resizeThrottleMs={resizeThrottle}
                loading={!state.fetched || resizeThrottle > 60}
                onProcessRowUpdateError={(error) => console.log({ error })}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                pagination
                autoPageSize
                slotProps={{
                  loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                  },
                }}
              />
            </Box>
          </Card>
        </Box>

        <APIStatusDialog
          open={apiStatus.open}
          status={apiStatus.status}
          message={apiStatus.message}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ClosureRulesDialog;
